<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <v-layout wrap>
      <v-flex class="xs12 sm6 md6 px-3">
        <v-text-field
          v-model.trim="parsedUsername"
          class="ft-username-auto-prepend"
          label="Username"
          :rules="rules().required('Username')"
          :prefix="$ftAPI().appMode()"
          :placeholder="' '"
          :error-messages="getPathErrors(jsonPath, 'username')"
          :readonly="isFormDisabled || itemData.id !== null"
          :hint="usernameHint()"
          persistent-hint
          required
          @input="clearPathErrors(jsonPath, 'username')"
        />

        <v-text-field
          v-model.trim="itemData.nameFirst"
          label="First Name"
          :rules="rules().required('First Name')"
          :error-messages="getPathErrors(jsonPath, 'nameFirst')"
          :disabled="isFormDisabled"
          required
          @input="clearPathErrors(jsonPath, 'nameFirst')"
        />

        <v-text-field
          v-model.trim="itemData.nameMi"
          class="sm3"
          label="Middle Initial"
          :error-messages="getPathErrors(jsonPath, 'nameMi')"
          :disabled="isFormDisabled"
          @input="clearPathErrors(jsonPath, 'nameMi')"
        />

        <v-text-field
          v-model.trim="itemData.nameLast"
          label="Last Name"
          :rules="rules().required('Last Name')"
          :error-messages="getPathErrors(jsonPath, 'nameLast')"
          :disabled="isFormDisabled"
          required
          @input="clearPathErrors(jsonPath, 'nameLast')"
        />

        <v-text-field
          v-model.trim="itemData.password"
          type="password"
          label="Password"
          :hint="passwordHint()"
          persistent-hint
          autocomplete="new-password"
          :rules="passwordRules"
          :error-messages="getPathErrors(jsonPath, 'password')"
          :disabled="isFormDisabled"
        />

        <v-text-field
          v-model.trim="password2"
          type="password"
          label="Confirm Password"
          :hint="passwordHint()"
          persistent-hint
          autocomplete="new-password"
          :rules="confirmPasswordRules"
          :error-messages="getPathErrors(jsonPath, 'password2')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.clearPin"
          invert
          label="Use PIN"
          persistent-hint
          :error-messages="getPathErrors(jsonPath, 'clearPin')"
          :disabled="isFormDisabled"
        />

        <template v-if="!itemData.clearPin">
          <v-text-field
            v-model.trim="itemData.pin"
            type="number"
            pattern="[0-9]"
            class="ft-fake-password"
            label="PIN"
            :hint="pinHint()"
            persistent-hint
            autocomplete="new-pin"
            :rules="pinRules"
            :disabled="isFormDisabled"
          />

          <v-text-field
            v-model.trim="pin2"
            type="number"
            pattern="[0-9]"
            class="ft-fake-password"
            label="Confirm PIN"
            :hint="pinHint()"
            persistent-hint
            autocomplete="new-pin"
            :rules="confirmPinRules"
            :disabled="isFormDisabled"
          />
        </template>

        <v-text-field
          v-model.trim="itemData.photoUrl"
          label="Photo"
          :error-messages="getPathErrors(jsonPath, 'photoUrl')"
          :disabled="isFormDisabled"
          required
          @input="clearPathErrors(jsonPath, 'photoUrl')"
        />
      </v-flex>

      <v-flex class="xs12 sm6 md6 px-3">
        <v-boolean
          v-if="$ftUser.isForeTeeUser && itemData.superAdmin"
          v-model="itemData.superAdmin"
          label="Super Admin"
          persistent-hint
          hint="DISABLE THIS NOW!"
          :error-messages="getPathErrors(jsonPath, 'superAdmin')"
          :field-errors="getPathErrors(jsonPath, 'superAdmin')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigClubconfig"
          label="Can Manage Club Settings"
          :error-messages="getPathErrors(jsonPath, 'sysconfigClubconfig')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigClubconfig')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigManageStaff"
          label="Can Manage Staff"
          :error-messages="getPathErrors(jsonPath, 'sysconfigManageStaff')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigManageStaff')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.reports"
          label="Can Access Reports"
          :error-messages="getPathErrors(jsonPath, 'reports')"
          :field-errors="getPathErrors(jsonPath, 'reports')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.viewReservations"
          label="Can View Reservations"
          :error-messages="getPathErrors(jsonPath, 'viewReservations')"
          :field-errors="getPathErrors(jsonPath, 'viewReservations')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.createReservations"
          label="Can Manage Reservations"
          :error-messages="getPathErrors(jsonPath, 'createReservations')"
          :field-errors="getPathErrors(jsonPath, 'createReservations')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.toolsMempref"
          label="Can Modify Member Preferences"
          :error-messages="getPathErrors(jsonPath, 'toolsMempref')"
          :field-errors="getPathErrors(jsonPath, 'toolsMempref')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.lottApprove"
          label="Can Approve Lotteries"
          :error-messages="getPathErrors(jsonPath, 'lottApprove')"
          :field-errors="getPathErrors(jsonPath, 'lottApprove')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigEvent"
          label="Can Manage Events"
          :error-messages="getPathErrors(jsonPath, 'sysconfigEvent')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigEvent')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.editEventCategories"
          label="Can Manage Event Categories"
          :error-messages="getPathErrors(jsonPath, 'editEventCategories')"
          :field-errors="getPathErrors(jsonPath, 'editEventCategories')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigKitchens"
          label="Can Manage Kitchens"
          :error-messages="getPathErrors(jsonPath, 'sysconfigKitchens')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigKitchens')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigLocations"
          label="Can Manage Locations"
          :error-messages="getPathErrors(jsonPath, 'sysconfigLocations')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigLocations')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigMealPeriods"
          label="Can Manage Meal Periods"
          :error-messages="getPathErrors(jsonPath, 'sysconfigMealPeriods')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigMealPeriods')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigBlockers"
          label="Can Manage Blockers"
          :error-messages="getPathErrors(jsonPath, 'sysconfigBlockers')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigBlockers')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.sysconfigOccasions"
          label="Can Manage Occasions"
          :error-messages="getPathErrors(jsonPath, 'sysconfigOccasions')"
          :field-errors="getPathErrors(jsonPath, 'sysconfigOccasions')"
          :disabled="isFormDisabled"
        />

        <v-boolean
          v-model="itemData.disabled"
          label="Enabled"
          invert
          :error-messages="getPathErrors(jsonPath, 'disabled')"
          :field-errors="getPathErrors(jsonPath, 'disabled')"
          :disabled="isFormDisabled"
        />
      </v-flex>
    </v-layout>
    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {itemEdit} from '@/ft-vue-core'

var itemDefault = {
  username: null,
  nameFirst: null,
  nameMi: '',
  nameLast: null,
  sysconfigClubconfig: false,
  reports: false,
  lottApprove: false,
  superAdmin: false,
  sysconfigEvent: false,
  createReservations: false,
  viewReservations: false,
  sysconfigKitchens: false,
  sysconfigLocations: false,
  sysconfigMealPeriods: false,
  sysconfigBlockers: false,
  sysconfigOccasions: false,
  sysconfigManageStaff: false,
  toolsMempref: false,
  password: null,
  disabled: false,
  id: null,
  activityId: 9999,
  recordLockExpiration: null,
  photoUrl: null,
  clearPin: false,
  hasPin: false,
  pin: null
}

const cloner = {
  id: null,
  username: null,
  nameFirst: null,
  nameMi: '',
  nameLast: null,
  password: null,
  pin: null
}

export default {
  name: 'StaffItem',
  components: {
  },
  mixins: [itemEdit],
  data: function () {
    return {
      type: 'Staff User',
      types: 'Staff Users',
      /*
      usernameRules: [
        // the username must start with the word 'dining'
        (v) => (v && v.slice(0, 6) === 'dining') || 'Must start with the word dining'
      ],
      */
      passwordRules: [
        // the password must either be empty or at least 8 chars
        (v) => !v || v && v.length >= 8 || 'must be at least 8 characters'
      ],
      confirmPasswordRules: [
        // both passowrd and the confirmed password must both be empty or they both must be equal
        (v) => !v && (!this.itemData.password || this.itemData.password === '') || !!v && v === this.itemData.password || 'Passwords must match'
      ],
      confirmPinRules: [
        // both passowrd and the confirmed password must both be empty or they both must be equal
        (v) => !!this.itemData.clearPin || this.itemData.pin === '' || this.itemData.pin === null || v === this.itemData.pin ||
         'Pins must match'
      ],
      password2: null,
      pin2: null,
      apiRecordCommand: 'staff',
      itemDefault: itemDefault
    }
  },
  computed: {
    pinRules () {
      const regexp = new RegExp('^[0-9]{' + this.$ftAC.pinLength + '}$')
      return [
        // the password must either be empty or at least 8 chars
        (v) => !!this.itemData.clearPin || !v || !!v && !!v.match(regexp) || 'Pin must be between 4 numbers'
      ]
    },
    pinInput () {
      return this.itemData.pin
    },
    cloner () {
      return this.$ftAccess.manageStaff ? cloner : null
    },
    isDeleteHidden () {
      return this.isNewItem
    },
    managedUserRegExp () {
      return new RegExp('^(' + this.$ftAPI().appMode() + ')', 'i')
    },
    isManagedUsername () {
      return this.isNewItem || this.itemData.username.match(this.managedUserRegExp)
    },
    parsedUsername: {
      get () {
        return this.itemData.username ? this.itemData.username.replace(this.managedUserRegExp, '') : this.itemData.username
      },
      set (val) {
        this.itemData.username = this.isManagedUsername ? this.$ftAPI().appMode() + val : val
      }
    }
  },
  watch: {
    pinInput (v) {
      const pinLength = this.$ftAC.pinLength
      if (v && v.length > pinLength) {
        setTimeout(() => this.itemData.pin = this.itemData.pin.substring(0, pinLength), 0)
      }
    }
  },
  methods: {
    passwordHint () {
      return this.itemData.id === null ? 'Must be specified' : 'Only if changing'
    },
    pinHint () {
      return 'Only if changing'
    },
    usernameHint () {
      return this.itemData.id === null ? '"' + this.$ftAPI().appMode() + '" is automatically prepended to name' : ''
    },
    afterLoad (item) {
      this.password2 = null
      this.pin2 = null
      console.warn('AfterStaff Load', item)
      if (!item.hasPin) {
        this.itemData.clearPin = true
      } else {
        this.itemData.clearPin = false
      }
    },
    modifyItemDataBeforeSave (item) {
      if (!this.$ftUser.isForeTeeUser) {
        // Don't allow setting superAdmin
        delete item.superAdmin
      }
    },
    itemCommandFilter (command) {
      if (this.$ftAC.requirePin) return this.addCommandParamArrayVal(command, 'include_fields', 'hasPin')
      return command
    }
  }
}
</script>
<style>
  .ft-username-auto-prepend .input-group--text-field__prefix {
    margin-right: 0;
  }
</style>
